import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";
import Iconify from "src/components/iconify";
import {
  useExportCsvTemplateMutation,
  useExportGoogleSheetMutation,
  useImportCsvMutation,
} from "src/context/api/activations/api";
import { RootState } from "src/context/store";
import { t } from "i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  matchType: "EXACT" | "OTHER";
  workspaceId: number;
  client: string;
  country: string;
  filters: GridFilterModel;
};

type DataEntry = { row: number; column: string; value: string; reason: string };

type PreviewRow = {
  type: "CREATE" | "UPDATE" | "NO_OP";
  line_no: number;
  platform: string;
  account_name: string;
  campaign_name: string;
  ad_group_name: string;
  keyword: string;
  match_type: string;
  new_cpc: number | null;
  old_cpc: number | null;
};

type ImportState =
  | { step: "upload" }
  | { step: "loading" }
  | { step: "error_format"; reason: string }
  | { step: "error_data"; errors: DataEntry[] }
  | { step: "preview"; rows: PreviewRow[]; warnings: DataEntry[]; file: File };

const BRAND_BLUE = "#312783";
const UPLOAD_BORDER = "rgba(120, 120, 228, 0.2)";
const UPLOAD_BG = "linear-gradient(89.79deg, #fdfdfd 18.81%, #fbfaff 83.53%)";

export default function ModalCsvImport({
  open,
  onClose,
  matchType,
  workspaceId,
  client,
  country,
  filters,
}: Props) {
  const [state, setState] = useState<ImportState>({ step: "upload" });
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [filterWarnings, setFilterWarnings] = useState(false);
  const [isOauthPending, setIsOauthPending] = useState(false);
  const [filterType, setFilterType] = useState<"CREATE" | "UPDATE" | "NO_OP" | null>(null);
  const [exportTemplate, { isLoading: isExporting }] = useExportCsvTemplateMutation();
  const [exportGoogleSheet, { isLoading: isExportingSheet }] = useExportGoogleSheetMutation();
  const [importCsv] = useImportCsvMutation();

  const reduxKeywords = useSelector((state: RootState) => state.filters.keywords);

  const buildFilters = useCallback((): GridFilterModel => {
    const muiItems = filters?.items ?? [];
    if (reduxKeywords.length === 0) return { items: muiItems };
    const kwItems = reduxKeywords.map((kw, i) => ({
      id: `kw_${i}`,
      field: "keyword",
      operator: "is",
      value: kw,
    }));
    const nonKwMui = muiItems.filter((it: any) => it.field !== "keyword");
    if (nonKwMui.length > 0) {
      return { items: [...nonKwMui, ...kwItems], logicOperator: "or" as any };
    }
    return { items: kwItems, logicOperator: "or" as any };
  }, [filters, reduxKeywords]);

  const resetAndClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDialogExited = useCallback(() => {
    setState({ step: "upload" });
    setShowGuidelines(false);
    setFilterWarnings(false);
    setFilterType(null);
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await exportTemplate({
        workspace_id: workspaceId,
        client,
        match_type: matchType,
        country,
        filters: buildFilters(),
      }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "keywords_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const reason = err?.data?.reason;
      if (reason === "too_many_rows") {
        enqueueSnackbar(t("csv_import.errors.format.too_many_rows"), { variant: "error" });
      } else {
        enqueueSnackbar(t("csv_import.server_error"), { variant: "error" });
      }
    }
  }, [exportTemplate, workspaceId, client, matchType, country, buildFilters]);

  const handleDownloadGoogleSheet = useCallback(async () => {
    try {
      const result = await exportGoogleSheet({
        workspace_id: workspaceId,
        client,
        match_type: matchType,
        country,
        filters: buildFilters(),
      }).unwrap();

      if ("url" in result) {
        window.open(result.url, "_blank");
        return;
      }

      if (result.status === "oauth_required") {
        const popup = window.open(result.auth_url, "_blank", "width=500,height=600");
        setIsOauthPending(true);

        const handler = (event: MessageEvent) => {
          if (event.data?.type !== "drive-auth-success") return;
          window.removeEventListener("message", handler);
          clearInterval(closedPoller);
          setIsOauthPending(false);
          exportGoogleSheet({
            workspace_id: workspaceId,
            client,
            match_type: matchType,
            country,
            filters: buildFilters(),
          }).unwrap().then((retryResult) => {
            if ("url" in retryResult) window.open(retryResult.url, "_blank");
          }).catch(() => {
            enqueueSnackbar(t("csv_import.server_error"), { variant: "error" });
          });
        };

        const closedPoller = setInterval(() => {
          if (popup?.closed) {
            clearInterval(closedPoller);
            window.removeEventListener("message", handler);
            setIsOauthPending(false);
          }
        }, 1000);

        window.addEventListener("message", handler);
      }
    } catch (err: any) {
      const reason = err?.data?.reason;
      if (reason === "too_many_rows") {
        enqueueSnackbar(t("csv_import.errors.format.too_many_rows"), { variant: "error" });
      } else {
        enqueueSnackbar(t("csv_import.server_error"), { variant: "error" });
      }
    }
  }, [exportGoogleSheet, workspaceId, client, matchType, country, buildFilters, t]);

  const runImport = useCallback(async (file: File, dryRun: boolean) => {
    setState({ step: "loading" });
    try {
      const result: any = await importCsv({
        file,
        workspace_id: workspaceId,
        client,
        match_type: matchType,
        country,
        filters: buildFilters(),
        dry_run: dryRun,
      }).unwrap();

      if (result.status === "success") {
        enqueueSnackbar(t("csv_import.success_snackbar"), { variant: "success" });
        resetAndClose();
        return;
      }
      if (result.status === "preview") {
        setFilterWarnings(false);
        setFilterType(null);
        setState({ step: "preview", rows: result.rows ?? [], warnings: result.warnings ?? [], file });
        return;
      }
      setState({ step: "error_format", reason: result.reason || "unknown" });
    } catch (err: any) {
      const data = err?.data;
      if (data?.status === "format_error") {
        setState({ step: "error_format", reason: data.reason || "unknown" });
      } else if (data?.status === "data_errors") {
        setState({ step: "error_data", errors: data.errors || [] });
      } else {
        enqueueSnackbar(t("csv_import.server_error"), { variant: "error" });
        setState({ step: "upload" });
      }
    }
  }, [importCsv, workspaceId, client, matchType, country, buildFilters, resetAndClose]);

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setState({ step: "error_format", reason: "invalid_extension" });
      return;
    }
    runImport(file, true);
  }, [runImport]);

  const handleDropRejected = useCallback(() => {
    setState({ step: "error_format", reason: "invalid_extension" });
  }, []);

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    accept: {
      "text/csv": [".csv"],
      "text/plain": [".csv"],
      "application/vnd.ms-excel": [".csv"],
      "application/csv": [".csv"],
    },
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    multiple: false,
  });

  // ── Shared sub-sections ───────────────────────────────────────────────────

  const renderSection1 = () => (
    <Box
      sx={{
        bgcolor: "rgba(223, 230, 246, 0.5)",
        borderRadius: "4px",
        px: 3,
        pt: 2.5,
        pb: 2,
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ color: "#333c47", lineHeight: "22px" }}>
          {t("csv_import.intro_text")}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          <Link
            component="button"
            underline="hover"
            onClick={handleDownloadTemplate}
            disabled={isExporting}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 14,
              fontWeight: 700,
              color: "#141567",
              lineHeight: "22px",
            }}
          >
            <Iconify icon="solar:arrow-down-outline" width={18} />
            {t("csv_import.download_template")}
          </Link>
          <Link
            component="button"
            underline="hover"
            onClick={handleDownloadGoogleSheet}
            disabled={isExportingSheet || isOauthPending}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 14,
              fontWeight: 700,
              color: "#141567",
              lineHeight: "22px",
              ml: 1,
            }}
          >
            <Iconify icon="solar:arrow-down-outline" width={18} />
            {t("csv_import.download_google_sheet")}
          </Link>
          {isOauthPending && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {t("csv_import.oauth_pending")}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const renderDropzone = (hasError = false) => (
    <Box
      {...getRootProps()}
      sx={{
        height: 185,
        borderRadius: "6px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: "none",
        border: `1px dashed ${hasError ? "#d32f2f" : isDragActive ? BRAND_BLUE : UPLOAD_BORDER}`,
        background: hasError ? "rgba(211,47,47,0.08)" : isDragActive ? "rgba(49,39,131,0.08)" : UPLOAD_BG,
        transition: "border-color 0.2s, background 0.2s",
        "&:hover": { opacity: 0.85 },
      }}
    >
      <input {...getInputProps()} />
      <Stack alignItems="center" spacing={1}>
        <Iconify
          icon="solar:cloud-upload-bold"
          width={40}
          sx={{ color: hasError ? "error.main" : BRAND_BLUE }}
        />
        <Typography
          variant="subtitle2"
          sx={{ color: BRAND_BLUE, fontWeight: 600, fontSize: 14 }}
        >
          {t("csv_import.choose_file")}
        </Typography>
        <Typography sx={{ color: "#919eab", fontSize: 12 }}>
          {t("csv_import.supported_types")}
        </Typography>
      </Stack>
    </Box>
  );

  const renderGuidelinesToggle = () => (
    <Box
      component="button"
      onClick={() => setShowGuidelines((v) => !v)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        height: 40,
        px: 0,
        background: "none",
        border: "none",
        borderBottom: showGuidelines ? "1px solid rgba(145, 158, 171, 0.2)" : "none",
        cursor: "pointer",
        color: "#919eab",
      }}
    >
      <Iconify
        icon={showGuidelines ? "solar:eye-bold" : "solar:eye-outline"}
        width={18}
        sx={{ color: "#919eab", flexShrink: 0 }}
      />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#919eab", lineHeight: "22px" }}>
        {showGuidelines ? t("csv_import.hide_guidelines") : t("csv_import.see_guidelines")}
      </Typography>
    </Box>
  );

  const GUIDELINES = [
    { key: "item1" },
    { key: "item2" },
    { key: "item3" },
    { key: "item4" },
  ];

  const renderGuidelinesContent = () => (
    <Stack spacing={2.5}>
      {GUIDELINES.map(({ key }) => (
        <Stack key={key} spacing={0.5}>
          <Typography
            component="li"
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: BRAND_BLUE,
              lineHeight: "18px",
              listStyleType: "disc",
              ml: "21px",
              pl: 0,
            }}
          >
            {t(`csv_import.guidelines.${key}.title`)}
          </Typography>
          <Box sx={{ pl: "18px" }}>
            {t(`csv_import.guidelines.${key}.body`)
              .split("\n")
              .map((line, i) => (
                <Typography
                  key={i}
                  sx={{ fontSize: 14, color: "#637381", lineHeight: "18px" }}
                >
                  {line}
                </Typography>
              ))}
          </Box>
        </Stack>
      ))}
    </Stack>
  );

  const renderUploadButton = () => (
    <Box display="flex" justifyContent="flex-end">
      <Button
        variant="contained"
        onClick={openFilePicker}
        startIcon={<Iconify icon="solar:upload-minimalistic-outline" width={16} />}
        sx={{
          bgcolor: "#181818",
          color: "#fff",
          fontWeight: 600,
          fontSize: 13,
          borderRadius: "8px",
          "&:hover": { bgcolor: "#333" },
        }}
      >
        {t("csv_import.upload_button")}
      </Button>
    </Box>
  );

  // ── Step renderers ────────────────────────────────────────────────────────

  const renderUpload = () => (
    <Stack spacing={3}>
      {renderSection1()}

      <Box sx={{ bgcolor: "white", borderRadius: 1, px: 3, py: 2.5 }}>
        <Stack spacing={1}>
          {renderDropzone()}
          {renderGuidelinesToggle()}
          {showGuidelines && renderGuidelinesContent()}
        </Stack>
      </Box>
    </Stack>
  );

  const renderLoading = () => (
    <Stack alignItems="center" spacing={2} py={6}>
      <CircularProgress />
      <Typography>{t("csv_import.loading")}</Typography>
    </Stack>
  );

  const renderErrorFormat = (reason: string) => (
    <Stack spacing={3}>
      {renderSection1()}

      <Box sx={{ bgcolor: "white", borderRadius: 1, px: 3, py: 2.5 }}>
        <Stack spacing={1}>
          {renderDropzone(true)}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="solar:close-circle-bold" width={18} sx={{ color: "error.main", flexShrink: 0 }} />
            <Typography variant="body2" color="error.main">
              {t(`csv_import.errors.format.${reason}`, { defaultValue: t("csv_import.server_error") })}
            </Typography>
          </Stack>
          {renderGuidelinesToggle()}
          {showGuidelines && renderGuidelinesContent()}
        </Stack>
      </Box>

      {renderUploadButton()}
    </Stack>
  );

  const renderErrorData = (errors: DataEntry[]) => (
    <Stack spacing={3}>
      {renderSection1()}

      <Box sx={{ bgcolor: "white", borderRadius: 1, px: 3, py: 2.5 }}>
        <Stack spacing={1}>
          {renderDropzone()}
        </Stack>

      <Stack spacing={1} paddingTop="12px">
        <Typography fontSize="12px">
          {t("csv_import.error_table.title", { count: errors.length }).toUpperCase()}
        </Typography>
        <Box sx={{ border: "1px solid rgba(224,224,224,1)", borderRadius: "8px", overflow: "hidden" }}>
          <Table size="small" sx={{ "& td, & th": { borderBottom: "1px solid rgba(224,224,224,1) !important" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{t("csv_import.error_table.column_line")}</TableCell>
                <TableCell sx={{ width: 90, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{t("csv_import.error_table.column_column")}</TableCell>
                <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>{t("csv_import.error_table.column_reason")}</TableCell>
                <TableCell sx={{ width: 130 }}>{t("csv_import.error_table.column_value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.map((e, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}><Typography fontSize="13px">{e.row}</Typography></TableCell>
                  <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}><Typography fontSize="13px">{e.column}</Typography></TableCell>
                  <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>
                    <Typography fontSize="13px">{t(`csv_import.errors.data.${e.reason}`)}</Typography>
                  </TableCell>
                  <TableCell><Typography fontSize="13px">{e.value}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
      {renderGuidelinesToggle()}
      {showGuidelines && renderGuidelinesContent()}
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button
          variant="contained"
          onClick={openFilePicker}
          startIcon={<Iconify icon="solar:upload-minimalistic-outline" width={16} />}
          sx={{ bgcolor: "#181818", "&:hover": { bgcolor: "#333" } }}
        >
          {t("csv_import.upload_button")}
        </Button>
      </Box>
    </Stack>
  );

  const renderCpcCell = (row: PreviewRow) => {
    if (row.type === "CREATE") {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={t("csv_import.preview.badge_new")}
            size="small"
            sx={{ bgcolor: "rgba(34,197,94,0.15)", color: "#166534", fontWeight: 700, height: 20 }}
          />
          <Typography sx={{ color: "#166534", fontWeight: 600, fontSize: 13 }}>
            {row.new_cpc}
          </Typography>
        </Box>
      );
    }
    if (row.type === "UPDATE") {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ textDecoration: "line-through", color: "#919eab", fontSize: 13 }}>
            {row.old_cpc}
          </Typography>
          <Typography sx={{ color: "#919eab", fontSize: 13 }}>→</Typography>
          <Typography sx={{ color: "#166534", fontWeight: 600, fontSize: 13 }}>
            {row.new_cpc}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography sx={{ fontSize: 13, color: "#333c47" }}>
        {row.new_cpc != null ? row.new_cpc : "—"}
      </Typography>
    );
  };

  const renderPreview = (rows: PreviewRow[], warnings: DataEntry[], file: File) => {
    const warningLineNos = new Set(warnings.map((w) => w.row));
    const createCount = rows.filter((r) => r.type === "CREATE").length;
    const updateCount = rows.filter((r) => r.type === "UPDATE").length;
    const noOpCount = rows.filter((r) => r.type === "NO_OP").length;
    const hasChanges = createCount + updateCount > 0;

    const filteredRows = rows.filter((row) => {
      if (filterType && row.type !== filterType) return false;
      if (filterWarnings && !warningLineNos.has(row.line_no)) return false;
      return true;
    });

    const filterChipSx = (bg: string, bgHover: string, color: string, active: boolean) => ({
      bgcolor: bg,
      color,
      fontWeight: 600,
      "&:hover": { bgcolor: bgHover },
      ...(active ? { outline: "2px solid currentColor", outlineOffset: -2 } : {}),
    });

    const toggleType = (type: "CREATE" | "UPDATE" | "NO_OP") =>
      setFilterType((v) => (v === type ? null : type));

    return (
      <Box display="flex" flexDirection="column" height="100%">
        {/* Header: counts as filters + warnings button + no-changes notice */}
        <Box sx={{ px: 3, pt: 3, pb: 1.5, flexShrink: 0 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip
                label={t("csv_import.preview.count_create", { count: createCount })}
                size="small"
                clickable
                onClick={() => toggleType("CREATE")}
                sx={filterChipSx("rgba(34,197,94,0.12)", "rgba(34,197,94,0.22)", "#166534", filterType === "CREATE")}
              />
              <Chip
                label={t("csv_import.preview.count_update", { count: updateCount })}
                size="small"
                clickable
                onClick={() => toggleType("UPDATE")}
                sx={filterChipSx("rgba(59,130,246,0.12)", "rgba(59,130,246,0.22)", "#1d4ed8", filterType === "UPDATE")}
              />
              <Chip
                label={t("csv_import.preview.count_noop", { count: noOpCount })}
                size="small"
                clickable
                onClick={() => toggleType("NO_OP")}
                sx={filterChipSx("rgba(145,158,171,0.12)", "rgba(145,158,171,0.22)", "#637381", filterType === "NO_OP")}
              />
              {warnings.length > 0 && (
                <Chip
                  label={t("csv_import.preview.filter_warnings")}
                  size="small"
                  clickable
                  onClick={() => setFilterWarnings((v) => !v)}
                  sx={filterChipSx("rgba(255,193,7,0.12)", "rgba(255,193,7,0.22)", "#b45309", filterWarnings)}
                />
              )}
            </Stack>

            {!hasChanges && (
              <Box sx={{ py: 1.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("csv_import.preview.no_changes")}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Scrollable table */}
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "auto", px: 3 }}>
          {filteredRows.length > 0 && (
            <Box sx={{ border: "1px solid rgba(224,224,224,1)", borderRadius: "4px", overflow: "hidden" }}>
              <Table size="small" sx={{ "& td, & th": { borderBottom: "1px solid rgba(224,224,224,1) !important" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{t("csv_import.error_table.column_line")}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Platform</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Account</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Campaign</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Ad Group</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Keyword</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>Match type</TableCell>
                    <TableCell>Max CPC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row, i) => (
                    <TableRow
                      key={i}
                      sx={warningLineNos.has(row.line_no) ? { bgcolor: "rgba(255,193,7,0.08)" } : undefined}
                    >
                      <TableCell sx={{ borderRight: "1px solid rgba(224,224,224,1) !important" }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography sx={{ fontSize: 12 }}>{row.line_no}</Typography>
                          {warningLineNos.has(row.line_no) && (
                            <Iconify icon="solar:danger-triangle-bold" width={14} sx={{ color: "warning.main" }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.platform}</TableCell>
                      <TableCell sx={{ fontSize: 12, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.account_name}</TableCell>
                      <TableCell sx={{ fontSize: 12, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.campaign_name}</TableCell>
                      <TableCell sx={{ fontSize: 12, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.ad_group_name}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 500, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.keyword}</TableCell>
                      <TableCell sx={{ fontSize: 12, borderRight: "1px solid rgba(224,224,224,1) !important" }}>{row.match_type}</TableCell>
                      <TableCell>{renderCpcCell(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {/* Sticky action bar */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={1}
          sx={{
            px: 3,
            py: 2,
            flexShrink: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button onClick={() => setState({ step: "upload" })}>
            {t("csv_import.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!hasChanges}
            onClick={() => runImport(file, false)}
            sx={{ bgcolor: "#181818", "&:hover": { bgcolor: "#333" }, "&.Mui-disabled": { bgcolor: "#e0e0e0" } }}
          >
            {t("csv_import.preview.confirm_button")}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderBody = () => {
    switch (state.step) {
      case "upload":       return renderUpload();
      case "loading":      return renderLoading();
      case "error_format": return renderErrorFormat(state.reason);
      case "error_data":   return renderErrorData(state.errors);
      case "preview":      return renderPreview(state.rows, state.warnings, state.file);
      default:             return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth={state.step === "preview" ? "xl" : "sm"}
      fullWidth
      PaperProps={state.step === "preview" ? { sx: { height: "85vh" } } : undefined}
      TransitionProps={{ onExited: handleDialogExited }}
    >
      <DialogTitle sx={{ px: 3, py: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#333c47" }}>
            {t("csv_import.modal_title")}
          </Typography>
          <IconButton onClick={resetAndClose} size="small">
            <Iconify icon="material-symbols:close-rounded" width={18} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={
          state.step === "preview"
            ? { p: 0, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#f7f9fb" }
            : { p: 3, bgcolor: "#f7f9fb" }
        }
      >
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
}
